//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { BodyText, SectionText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import RequestModalSuccessIcon from '@images/campaigns/sampling/request-modal-success.svg';

export type RequestCampaignSuccessModalVariant =
  | 'submitted'
  | 'contact_requested';

interface RequestCampaignSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: RequestCampaignSuccessModalVariant;
}

export const RequestCampaignSuccessModal = ({
  isOpen,
  onClose,
  variant = 'submitted',
}: RequestCampaignSuccessModalProps) => {
  const { samplingCampaignRequest } = useSamplingCampaign();

  return (
    <Modal
      closeModal={onClose}
      contents={
        <Container>
          <img alt='Success' src={RequestModalSuccessIcon} />
          <ContainerHeader>
            <SectionText>Campaign requested successfully:</SectionText>
            <SectionText>{samplingCampaignRequest?.name}</SectionText>
          </ContainerHeader>
          <BodyText>
            {variant === 'contact_requested'
              ? 'Your campaign has been requested. We will be in touch with you soon.'
              : 'Your next step is to log into Support Community where you will track your remaining tasks, shipping information and campaign timeline'}
          </BodyText>
        </Container>
      }
      isFooterEnabled
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: 'Close',
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_CAMPAIGN_MODAL_SUCCESS_BUTTON,
        onClick: onClose,
      }}
      shouldIncludeSecondaryButton={false}
      shouldIncludeTertiaryButton={false}
      title=''
      width='wide'
    />
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ContainerHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
  align-items: center;
  justify-content: center;
`;
